'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { useAuth } from '@/contexts/AuthContext';
import Icon from '@/components/ui/AppIcon';
import SwaggerUI from 'swagger-ui-react';
import 'swagger-ui-react/swagger-ui.css';

const swaggerSpec = {
  openapi: '3.0.0',
  info: {
    title: 'DataConvert API',
    version: '1.0.0',
    description: 'Convert files between formats with our powerful API. Supports PDF, Excel, CSV, XML, and JSON conversions.',
    contact: {
      name: 'DataConvert Support',
      email: 'support@dataconvert.com',
    },
  },
  servers: [
    {
      url: 'https://api.dataconvert.com/v1',
      description: 'Production server',
    },
    {
      url: 'https://staging-api.dataconvert.com/v1',
      description: 'Staging server',
    },
  ],
  components: {
    securitySchemes: {
      bearerAuth: {
        type: 'http',
        scheme: 'bearer',
        bearerFormat: 'JWT',
      },
      apiKey: {
        type: 'apiKey',
        in: 'header',
        name: 'X-API-Key',
      },
    },
    schemas: {
      ConversionRequest: {
        type: 'object',
        required: ['file', 'target_format'],
        properties: {
          file: {
            type: 'string',
            format: 'binary',
            description: 'The file to convert',
          },
          target_format: {
            type: 'string',
            enum: ['json', 'pdf', 'excel', 'csv', 'xml'],
            description: 'Target format for conversion',
          },
          options: {
            type: 'object',
            properties: {
              preserve_formatting: {
                type: 'boolean',
                default: true,
              },
              include_metadata: {
                type: 'boolean',
                default: false,
              },
            },
          },
        },
      },
      ConversionResponse: {
        type: 'object',
        properties: {
          transaction_id: {
            type: 'string',
            format: 'uuid',
            description: 'Unique transaction identifier',
          },
          status: {
            type: 'string',
            enum: ['pending', 'processing', 'completed', 'failed'],
          },
          download_url: {
            type: 'string',
            format: 'uri',
            description: 'URL to download converted file (available when status is completed)',
          },
          processing_time_ms: {
            type: 'integer',
            description: 'Time taken to process the conversion in milliseconds',
          },
          file_size_kb: {
            type: 'integer',
            description: 'Size of the converted file in kilobytes',
          },
          created_at: {
            type: 'string',
            format: 'date-time',
          },
        },
      },
      Transaction: {
        type: 'object',
        properties: {
          id: {
            type: 'string',
            format: 'uuid',
          },
          format_type: {
            type: 'string',
            enum: ['pdf_to_json', 'excel_to_json', 'csv_to_json', 'xml_to_json', 'json_to_pdf', 'json_to_excel'],
          },
          status: {
            type: 'string',
            enum: ['pending', 'processing', 'completed', 'failed'],
          },
          file_size_kb: {
            type: 'integer',
          },
          processing_time_ms: {
            type: 'integer',
          },
          amount: {
            type: 'number',
            format: 'float',
          },
          created_at: {
            type: 'string',
            format: 'date-time',
          },
          completed_at: {
            type: 'string',
            format: 'date-time',
            nullable: true,
          },
        },
      },
      Error: {
        type: 'object',
        properties: {
          error: {
            type: 'string',
            description: 'Error message',
          },
          code: {
            type: 'string',
            description: 'Error code',
          },
          details: {
            type: 'object',
            description: 'Additional error details',
          },
        },
      },
    },
  },
  security: [
    {
      bearerAuth: [],
    },
  ],
  paths: {
    '/convert': {
      post: {
        summary: 'Convert a file',
        description: 'Upload a file and convert it to the specified target format',
        tags: ['Conversions'],
        requestBody: {
          required: true,
          content: {
            'multipart/form-data': {
              schema: {
                $ref: '#/components/schemas/ConversionRequest',
              },
            },
          },
        },
        responses: {
          '200': {
            description: 'Conversion initiated successfully',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/ConversionResponse',
                },
              },
            },
          },
          '400': {
            description: 'Invalid request',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/Error',
                },
              },
            },
          },
          '401': {
            description: 'Unauthorized',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/Error',
                },
              },
            },
          },
          '429': {
            description: 'Rate limit exceeded',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/Error',
                },
              },
            },
          },
        },
      },
    },
    '/convert/{transaction_id}': {
      get: {
        summary: 'Get conversion status',
        description: 'Retrieve the status and details of a conversion transaction',
        tags: ['Conversions'],
        parameters: [
          {
            name: 'transaction_id',
            in: 'path',
            required: true,
            schema: {
              type: 'string',
              format: 'uuid',
            },
            description: 'Transaction ID returned from the conversion request',
          },
        ],
        responses: {
          '200': {
            description: 'Transaction details retrieved successfully',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/ConversionResponse',
                },
              },
            },
          },
          '404': {
            description: 'Transaction not found',
            content: {
              'application/json': {
                schema: {
                  $ref: '#/components/schemas/Error',
                },
              },
            },
          },
        },
      },
    },
    '/transactions': {
      get: {
        summary: 'List transactions',
        description: 'Retrieve a list of all conversion transactions for the authenticated user',
        tags: ['Transactions'],
        parameters: [
          {
            name: 'limit',
            in: 'query',
            schema: {
              type: 'integer',
              default: 50,
              maximum: 100,
            },
            description: 'Number of transactions to return',
          },
          {
            name: 'offset',
            in: 'query',
            schema: {
              type: 'integer',
              default: 0,
            },
            description: 'Number of transactions to skip',
          },
          {
            name: 'status',
            in: 'query',
            schema: {
              type: 'string',
              enum: ['pending', 'processing', 'completed', 'failed'],
            },
            description: 'Filter by transaction status',
          },
          {
            name: 'format_type',
            in: 'query',
            schema: {
              type: 'string',
              enum: ['pdf_to_json', 'excel_to_json', 'csv_to_json', 'xml_to_json', 'json_to_pdf', 'json_to_excel'],
            },
            description: 'Filter by format type',
          },
        ],
        responses: {
          '200': {
            description: 'Transactions retrieved successfully',
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  properties: {
                    transactions: {
                      type: 'array',
                      items: {
                        $ref: '#/components/schemas/Transaction',
                      },
                    },
                    total: {
                      type: 'integer',
                      description: 'Total number of transactions',
                    },
                    limit: {
                      type: 'integer',
                    },
                    offset: {
                      type: 'integer',
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
    '/analytics': {
      get: {
        summary: 'Get analytics',
        description: 'Retrieve analytics data for the authenticated user',
        tags: ['Analytics'],
        parameters: [
          {
            name: 'start_date',
            in: 'query',
            schema: {
              type: 'string',
              format: 'date',
            },
            description: 'Start date for analytics period (ISO 8601 format)',
          },
          {
            name: 'end_date',
            in: 'query',
            schema: {
              type: 'string',
              format: 'date',
            },
            description: 'End date for analytics period (ISO 8601 format)',
          },
        ],
        responses: {
          '200': {
            description: 'Analytics data retrieved successfully',
            content: {
              'application/json': {
                schema: {
                  type: 'object',
                  properties: {
                    total_transactions: {
                      type: 'integer',
                    },
                    completed_transactions: {
                      type: 'integer',
                    },
                    total_amount: {
                      type: 'number',
                      format: 'float',
                    },
                    avg_processing_time_ms: {
                      type: 'number',
                      format: 'float',
                    },
                    format_distribution: {
                      type: 'array',
                      items: {
                        type: 'object',
                        properties: {
                          format_type: {
                            type: 'string',
                          },
                          count: {
                            type: 'integer',
                          },
                          amount: {
                            type: 'number',
                            format: 'float',
                          },
                        },
                      },
                    },
                  },
                },
              },
            },
          },
        },
      },
    },
  },
};

export default function ApiDocsPage() {
  const { user, loading: authLoading, signOut } = useAuth();
  const router = useRouter();
  const [error, setError] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      router.push('/portal/login');
    }
  }, [user, authLoading, router]);

  const handleSignOut = async () => {
    try {
      await signOut();
      router.push('/portal/login');
    } catch (err: any) {
      setError(err.message || 'Failed to sign out');
    }
  };

  if (authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon name="ArrowPathIcon" size={48} className="text-primary animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading API documentation...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link href="/homepage" className="flex items-center gap-2">
            <Image src="/assets/images/ChatGPT_Image_13._feb._2026_10.17.16-1771010403596.png" alt="DataConvert Logo" width={40} height={40} className="object-contain" />
            <span className="text-xl font-serif text-foreground">DataConvert</span>
          </Link>

          <nav className="flex items-center gap-6">
            <Link href="/portal/dashboard" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
              Dashboard
            </Link>
            <Link href="/portal/api-docs" className="text-sm font-medium text-primary">
              API Docs
            </Link>
            <button
              onClick={handleSignOut}
              className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              Sign Out
            </button>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h1 className="text-4xl font-serif text-foreground mb-2">API Documentation</h1>
          <p className="text-muted-foreground">Complete reference for the DataConvert API</p>
        </div>

        {error && (
          <div className="mb-8 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <p className="text-sm text-red-500">{error}</p>
          </div>
        )}

        {/* Quick Start Guide */}
        <div className="bg-card border border-border rounded-xl p-6 mb-8">
          <h2 className="text-xl font-serif text-foreground mb-4">Quick Start</h2>
          <div className="space-y-4">
            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">1. Authentication</h3>
              <p className="text-sm text-muted-foreground mb-2">
                All API requests require authentication using a Bearer token or API key:
              </p>
              <pre className="bg-background border border-border rounded-lg p-4 text-sm overflow-x-auto">
                <code className="text-primary">
                  {`curl -X POST https://api.dataconvert.com/v1/convert \\
  -H "Authorization: Bearer YOUR_TOKEN" \ -F"file=@document.pdf"\ -F"target_format=json"`}
                </code>
              </pre>
            </div>

            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">2. Rate Limits</h3>
              <p className="text-sm text-muted-foreground">
                Free tier: 100 requests/hour | Pro tier: 1,000 requests/hour | Enterprise: Unlimited
              </p>
            </div>

            <div>
              <h3 className="text-sm font-medium text-foreground mb-2">3. Response Format</h3>
              <p className="text-sm text-muted-foreground">
                All responses are returned in JSON format with appropriate HTTP status codes.
              </p>
            </div>
          </div>
        </div>

        {/* Swagger UI */}
        <div className="bg-card border border-border rounded-xl overflow-hidden swagger-container">
          <SwaggerUI spec={swaggerSpec} />
        </div>
      </main>

      <style jsx global>{`
        .swagger-container .swagger-ui {
          font-family: inherit;
        }
        .swagger-ui .topbar {
          display: none;
        }
        .swagger-ui .info {
          margin: 2rem;
        }
        .swagger-ui .scheme-container {
          background: transparent;
          box-shadow: none;
          padding: 0 2rem;
        }
      `}</style>
    </div>
  );
}